import React, { useEffect, useState, useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import { AsyncPaginate } from 'react-select-async-paginate';

import colors from 'theme/Colors.module.scss';
import './AdTemplateDropdown.scss';

import {
  useLazyGetTemplatesQuery,
  useLazyGetTemplateByIdQuery,
} from 'modules/templates/templatesApi';

const AdTemplateDropdown = props => {
  const { initialForm, onChange, isDisabled } = props;

  const [selectedOption, setSelectedOption] = useState({});

  const [getTemplates] = useLazyGetTemplatesQuery();
  const [getTemplateById] = useLazyGetTemplateByIdQuery();

  const defaultOptions = useMemo(
    () => [
      {
        id: '',
        label: 'None',
        is_portrait: true,
      },
    ],
    []
  );

  const mapTemplateToOption = template => {
    return {
      key: template.id,
      value: template.id,
      label: template.name,
      image:
        template.double_sided === false && template.bg_img
          ? [template.bg_img.thumb_url]
          : template.double_sided === true &&
            template.ad_template_pages.length > 1
          ? [...template.ad_template_pages]
              .sort((a, b) => a.page_num - b.page_num)
              .map(page => (page.bg_img ? page.bg_img.thumb_url : null))
          : null,
    };
  };

  useEffect(() => {
    let isMounted = true;

    const fetchTemplate = async () => {
      try {
        const currentTemplate = await getTemplateById(
          initialForm.adTemplate
        ).unwrap();
        if (isMounted) {
          setSelectedOption(mapTemplateToOption(currentTemplate));
        }
      } catch (err) {
        console.error('Failed to fetch template:', err);
      }
    };

    if (initialForm.adTemplate) {
      fetchTemplate();
    } else {
      setSelectedOption(defaultOptions[0]);
    }

    return () => {
      isMounted = false; // avoid setting state after unmount
    };
  }, [initialForm.adTemplate, defaultOptions, getTemplateById]);

  const onOptionChange = option => {
    setSelectedOption(option);
    onChange(option);
  };

  const ExistingAdTemplateOption = ({
    innerProps,
    isFocused,
    isSelected,
    data,
    children,
  }) => {
    const previewImg = data.image;

    return (
      <div className="adTemplate__list-item" key={data.value} {...innerProps}>
        <div
          className={`${isFocused ? 'focused' : ''} ${
            isSelected ? 'selected' : ''
          } list-item__wrapper`}
        >
          <div className="list-item__text">
            <span>{children}</span>
          </div>
          <div className="list-item__img">
            {previewImg &&
              previewImg.map((imageUrl, index) => {
                return (
                  imageUrl && (
                    <div key={index} className="list-item__img-wrapper">
                      <Box className="preview-img" bgImage={imageUrl} />
                    </div>
                  )
                );
              })}
          </div>
        </div>
      </div>
    );
  };

  const loadOptions = async (search, loadedOptions, { page }) => {
    const { data: templatesData } = await getTemplates({
      search,
      page: page || 1,
      size: 100,
    });

    // Transform raw API response
    const mappedOptions =
      templatesData?.data?.entities
        ?.map(template => mapTemplateToOption(template))
        .sort((a, b) => {
          const firstOption = b.label === defaultOptions[0].label;

          if (!firstOption) {
            if (a.label > b.label) return 1;
            if (a.label < b.label) return -1;
          }

          return 0;
        }) || [];

    let filteredOptions = [];

    if (!search) {
      filteredOptions = mappedOptions;
    } else {
      const searchLower = search.toLowerCase();
      filteredOptions = mappedOptions.filter(({ label }) =>
        label.toLowerCase().includes(searchLower)
      );
    }

    return {
      options: filteredOptions,
      hasMore: templatesData?.has_next,
      additional: {
        page: templatesData?.current_page + 1,
      },
    };
  };

  const customStyles = useMemo(
    () => ({
      control: (provided, state) => ({
        ...provided,
        width: '100%',
        borderColor: state.isFocused ? colors.primary900 : provided.borderColor,
        boxShadow: state.isFocused ? `0 0 0 1px ${colors.primary500}` : 'none',
        '&:hover': {
          borderColor: colors.primary900,
        },
      }),
    }),
    []
  );

  return (
    <AsyncPaginate
      className="adTemplateDropdown"
      inputId="adTemplateInput"
      isClearable
      cacheOptions
      defaultOptions={defaultOptions}
      loadOptions={loadOptions}
      components={{ Option: ExistingAdTemplateOption }}
      additional={{
        page: 1,
      }}
      debounceTimeout={400}
      //   menuIsOpen={true} // force menu open for debug purpose
      value={selectedOption}
      onChange={onOptionChange}
      styles={customStyles}
      isDisabled={isDisabled}
    />
  );
};

export default AdTemplateDropdown;
